import { useContext, useState } from 'react';
import { PortalCtx } from '../state';
import { Copy as CopyIcon, Eye, EyeOff, Mail, OctagonAlert } from 'lucide-react';
import { dateTimeLabel, sizeLabel, usd, type AccountView } from '@ck/sdk';
import { api, DEMO } from '../api';
import { Badge, Meter } from '../ui';

const RESULT: Record<string, string> = { passed: 'passed', failed: 'breached', reinstated: 'reinstated by support', reset: 'restarted', moved: 'moved by support' };

function CopyBtn({ text, label }: { text: string; label: string }) {
  const [ok, setOk] = useState(false);
  return <button type="button" className="copy-i" aria-label={`Copy ${label}`} title={ok ? 'Copied' : `Copy ${label}`} onClick={async () => { try { await navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 1200); } catch { /* blocked */ } }}><CopyIcon /></button>;
}

interface Creds { password: string; investorPassword: string }
const credCache = new Map<string, Creds>();

/** Trading passwords, hidden until the client asks to see them. */
export function Secret({ accountId, which, label }: { accountId: string; which: keyof Creds; label: string }) {
  const [c, setC] = useState<Creds | null>(credCache.get(accountId) ?? null);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  async function toggle() {
    if (show) { setShow(false); return; }
    if (!c) {
      setBusy(true);
      try { const r = await api.get<Creds>(`/v1/me/accounts/${accountId}/credentials`); credCache.set(accountId, r); setC(r); }
      catch { /* shown as hidden */ } finally { setBusy(false); }
    }
    setShow(true);
  }
  return (
    <span className="secret">
      <span className="mono">{show && c ? c[which] : '••••••••'}</span>
      <button type="button" className="copy-i" aria-label={show ? `Hide ${label}` : `Show ${label}`} title={show ? 'Hide' : 'Show'} disabled={busy} onClick={() => void toggle()}>{show ? <EyeOff /> : <Eye />}</button>
      {show && c && <CopyBtn text={c[which]} label={label} />}
    </span>
  );
}

export function EmailCreds({ accountId }: { accountId: string }) {
  const [state, setState] = useState<'idle' | 'busy' | 'sent' | 'err'>('idle');
  return <button type="button" className="link cred-mail" disabled={state === 'busy' || state === 'sent'} onClick={async () => { setState('busy'); try { await api.post(`/v1/me/accounts/${accountId}/credentials/email`, {}); setState('sent'); } catch { setState('err'); } }}><Mail />{state === 'sent' ? 'Sent to your email' : state === 'err' ? 'Could not send, try again' : 'Email me these details'}</button>;
}

/** Plain-language explanation of which rule ended the account, from the breach record. */
function BreachExplainer({ view }: { view: AccountView }) {
  const a = view.account;
  const last = [...a.history].reverse().find((h) => h.result === 'failed');
  if (!last) return null;
  const phase = a.plan.phases.find((p) => p.key === last.phaseKey) ?? a.plan.phases[a.phaseIndex]!;
  const r = phase.rules;
  const detail = last.detail ?? '';
  const rule = /daily/i.test(detail) ? 'Daily loss limit' : /max|overall|maximum/i.test(detail) ? 'Maximum loss limit' : /time|days/i.test(detail) ? 'Time limit' : /risk team|closed by/i.test(detail) ? 'Closed after review' : 'Rule breach';
  const limit = rule === 'Daily loss limit' && r.maxDailyLossPct ? `${r.maxDailyLossPct}% of ${usd(a.size)} (${usd((a.size * r.maxDailyLossPct) / 100)})`
    : rule === 'Maximum loss limit' ? `${r.maxLossPct}% ${r.maxLossType === 'trailing' ? 'trailing' : 'static'} (${usd((a.size * r.maxLossPct) / 100)})`
    : rule === 'Time limit' && r.maxCalendarDays ? `${r.maxCalendarDays} calendar days` : null;
  return (
    <div className="explain" role="note">
      <div className="explain-h"><OctagonAlert />Why this account was closed</div>
      <dl>
        <dt>Rule</dt><dd>{rule}</dd>
        {limit && <><dt>Limit</dt><dd>{limit}</dd></>}
        <dt>When</dt><dd>{dateTimeLabel(last.at)} ({Intl.DateTimeFormat().resolvedOptions().timeZone})</dd>
        <dt>Phase</dt><dd>{phase.name}, login {last.login}</dd>
        <dt>Detail</dt><dd>{detail || 'No further detail recorded.'}</dd>
      </dl>
    </div>
  );
}

export function AccountCard({ view, onChange }: { view: AccountView; onChange: () => void }) {
  const { account: a, evaluation: e, snapshot: s } = view;
  const phase = a.plan.phases[a.phaseIndex]!;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const sim = async (action: Record<string, unknown>) => {
    setBusy(true); setErr(null);
    try { await api.post(`/v1/dev/accounts/${a.id}/sim`, action); }
    catch (x) { setErr(x instanceof Error ? x.message : 'Simulator unavailable'); }
    finally { setBusy(false); onChange(); }
  };
  const one = a.size / 100;
  const portal = useContext(PortalCtx);
  return (
    <article className={`acc ${a.status}`}>
      <header>
        <div><span className="mono">{a.plan.name} {sizeLabel(a.size)}</span><h3>{phase.name}</h3></div>
        <Badge status={a.status} />
      </header>
      {a.status === 'failed' ? (
        <div className="acc-closed">
          <p>This account is closed, so its login no longer works. {/funding refused/i.test(a.history.at(-1)?.detail ?? '') ? 'Our risk team did not approve it for the funded stage; the reason is below.' : ''} Start again with a new challenge whenever you are ready.</p>
          <button className="btn sm" onClick={() => portal?.go('buy')}>Buy a new challenge</button>
        </div>
      ) : (
      <dl className="cred">
        <div><dt>Login</dt><dd>{a.login}<CopyBtn text={a.login} label="login" /></dd></div>
        <div><dt>Server</dt><dd>{a.server}<CopyBtn text={a.server} label="server" /></dd></div>
        <div><dt>Password</dt><dd><Secret accountId={a.id} which="password" label="password" /></dd></div>
        <div><dt>Investor password</dt><dd><Secret accountId={a.id} which="investorPassword" label="investor password" /></dd></div>
        {s && <div><dt>Balance</dt><dd>{usd(s.balance)}</dd></div>}
        {s && <div><dt>Equity</dt><dd>{usd(s.equity)}</dd></div>}
      </dl>
      )}
      {a.status !== 'failed' && <EmailCreds accountId={a.id} />}
      {e && a.status !== 'funding_review' && <BreachGauge daily={e.dailyLossUsedPct} total={e.maxLossUsedPct} />}
      {a.status === 'funding_review' && <p className="msg info">You passed. Our risk team is reviewing the account before your funded account opens. This usually takes less than one business day.</p>}
      {e && (
        <div className="meters">
          <Meter label="Profit target" value={e.targetProgressPct} sub={e.profitTargetLevel ? `Close at ${usd(e.profitTargetLevel)} balance` : undefined} />
          <Meter label="Daily loss used" value={e.dailyLossUsedPct} sub={e.dailyLossFloor ? `Stay above ${usd(e.dailyLossFloor)} today` : undefined} danger />
          <Meter label="Max loss used" value={e.maxLossUsedPct} sub={`Stay above ${usd(e.maxLossFloor)}`} danger />
          <div className="days mono">Trading days {e.tradingDays}{e.minTradingDays ? ` of ${e.minTradingDays} required` : ''}{phase.rules.maxCalendarDays ? `, day ${e.daysElapsed} of ${phase.rules.maxCalendarDays}` : ''}</div>
        </div>
      )}
      {a.status === 'failed' && <BreachExplainer view={view} />}
      {a.history.length > 0 && <ol className="hist">{a.history.map((h, i) => <li key={i} className={h.result}>{a.plan.phases.find((p) => p.key === h.phaseKey)?.name ?? h.phaseKey} {RESULT[h.result] ?? h.result}</li>)}</ol>}
      {DEMO && a.status !== 'failed' && (
        <div className="simbar" aria-label="Demo simulator">
          <span className="mono">Demo controls</span>
          <button className="btn ghost sm" disabled={busy} onClick={() => sim({ realise: one, newDay: true })}>Win day +1%</button>
          <button className="btn ghost sm" disabled={busy} onClick={() => sim({ realise: -one / 2, newDay: true })}>Lose day −0.5%</button>
          <button className="btn ghost sm" disabled={busy} onClick={() => sim({ floating: -2 * one })}>Open −2%</button>
          <button className="btn ghost sm" disabled={busy} onClick={() => sim({ close: true })}>Close all</button>
        </div>
      )}
      {err && <p className="msg err">{err}</p>}
    </article>
  );
}

/** Two rings: how much of today's and of the overall loss allowance is used, and a plain breach-risk verdict. */
function BreachGauge({ daily, total }: { daily: number | null; total: number }) {
  const worst = Math.max(daily ?? 0, total);
  const level = worst >= 70 ? 'high' : worst >= 50 ? 'mid' : 'low';
  const R1 = 42, R2 = 32, C1 = 2 * Math.PI * R1, C2 = 2 * Math.PI * R2;
  const d = Math.min(100, Math.max(0, daily ?? 0)), t = Math.min(100, Math.max(0, total));
  return (
    <div className={`gauge g-${level}`}>
      <svg viewBox="0 0 100 100" role="img" aria-label={`Breach risk ${Math.round(worst)}%: daily loss ${Math.round(d)}% used, max loss ${Math.round(t)}% used`}>
        <circle cx="50" cy="50" r={R1} className="g-track" /><circle cx="50" cy="50" r={R1} className="g-daily" strokeDasharray={`${(d / 100) * C1} ${C1}`} />
        <circle cx="50" cy="50" r={R2} className="g-track" /><circle cx="50" cy="50" r={R2} className="g-max" strokeDasharray={`${(t / 100) * C2} ${C2}`} />
        <text x="50" y="49" textAnchor="middle" className="g-n">{Math.round(worst)}%</text>
        <text x="50" y="62" textAnchor="middle" className="g-l">breach risk</text>
      </svg>
      <div className="g-text">
        <b>{level === 'high' ? 'High chance of breach' : level === 'mid' ? 'Getting close' : 'Low risk'}</b>
        <span><i className="dot daily" />Daily loss used {daily === null ? 'off' : `${Math.round(d)}%`}</span>
        <span><i className="dot max" />Max loss used {Math.round(t)}%</span>
        {level !== 'low' && <small>{level === 'high' ? 'Reduce position size or close losing trades now.' : 'Trade carefully for the rest of the day.'}</small>}
      </div>
    </div>
  );
}
